/**
 * Settings > Case Preparation > Drafting: the firm's drafting templates from
 * Firm Knowledge, by letter type and visa. An admin marks one default per
 * letter type and visa; Draft Letters starts from it. The mark is the entry
 * tag `default:<visa>`, written with the library's ordinary entry edit. One
 * Exhibit List default (`default:any`) styles every exhibit list CaseBlink
 * writes; without one, CaseBlink Standard.
 */
import { ArrowUpRight, FileText } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { firmKnowledgeApi } from "@/api/firm-knowledge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import {
  draftingGroups,
  exhibitListGroup,
  markDefaultEdits,
  type TemplateGroup
} from "@/features/caseIde/workflows/defaultTemplates";
import { firmKnowledgePath } from "@/features/firmKnowledge/routes";
import type { FirmLibraryEntry } from "@/features/firmKnowledge/types";
import { announceFirmUpdated } from "@/features/lawFirms/firmEvents";

import { CasePrepRow, CasePrepSection } from "./casePrepParts";

const STANDARD_LIST = "CaseBlink Standard";

type Props = {
  lawFirmId: string;
  canEdit: boolean;
  /** Close Settings before following a link into the app. */
  onNavigate?: () => void;
};

type ChoiceProps = {
  isDefault: boolean;
  canEdit: boolean;
  busy: boolean;
  onToggle: () => void;
  testId: string;
};

const DefaultChoice: FC<ChoiceProps> = ({
  isDefault,
  canEdit,
  busy,
  onToggle,
  testId
}) => (
  <div className="flex shrink-0 items-center gap-1">
    {isDefault ? (
      <StatusBadge tone="success" dot>
        Default
      </StatusBadge>
    ) : null}
    {canEdit ? (
      <Button
        variant="ghost"
        size="xs"
        disabled={busy}
        onClick={onToggle}
        data-testid={testId}
      >
        {isDefault ? "Unmark" : "Make Default"}
      </Button>
    ) : null}
  </div>
);

type EntryRowProps = {
  entry: FirmLibraryEntry;
  group: TemplateGroup;
  canEdit: boolean;
  busy: boolean;
  onMark: (group: TemplateGroup, entryId: string | null) => void;
  onNavigate?: () => void;
};

const EntryRow: FC<EntryRowProps> = ({
  entry,
  group,
  canEdit,
  busy,
  onMark,
  onNavigate
}) => {
  const isDefault = group.defaultId === entry.id;
  return (
    <li className="flex min-h-6 items-center justify-between gap-4">
      <Link
        to={firmKnowledgePath({ entryId: entry.id })}
        onClick={onNavigate}
        className="flex min-w-0 items-center gap-1 text-sm text-fg-2 hover:text-fg"
      >
        <span className="truncate">{entry.name}</span>
        <ArrowUpRight className="size-3 shrink-0 text-fg-4" />
      </Link>
      <DefaultChoice
        isDefault={isDefault}
        canEdit={canEdit}
        busy={busy}
        onToggle={() => onMark(group, isDefault ? null : entry.id)}
        testId={`drafting-mark-${entry.id}`}
      />
    </li>
  );
};

export const DraftingTemplatesSection: FC<Props> = ({
  lawFirmId,
  canEdit,
  onNavigate
}) => {
  const [entries, setEntries] = useState<FirmLibraryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await firmKnowledgeApi.listLibrary({ lawFirmId }, "active");
      setEntries(data.entries);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not load the templates."
      );
    }
  }, [lawFirmId]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo(() => draftingGroups(entries ?? []), [entries]);
  const exhibitLists = useMemo(
    () => exhibitListGroup(entries ?? []),
    [entries]
  );

  const mark = async (group: TemplateGroup, entryId: string | null) => {
    const edits = markDefaultEdits(group, entryId);
    if (!edits.length) return;
    setBusy(group.key);
    try {
      const updated = await Promise.all(
        edits.map((e) =>
          firmKnowledgeApi.updateEntry(e.id, { tags: e.tags }, { lawFirmId })
        )
      );
      setEntries((cur) =>
        (cur ?? []).map((e) => updated.find((u) => u.id === e.id) ?? e)
      );
      announceFirmUpdated(lawFirmId);
      const name =
        group.entries.find((e) => e.id === entryId)?.name ??
        (group.key === exhibitLists.key ? STANDARD_LIST : null);
      toast.success(
        name
          ? `"${name}" is the default ${group.label}`
          : `No default ${group.label}`
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not mark the default."
      );
      void load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <CasePrepSection
      title="Drafting"
      description="The template Draft Letters starts from, per letter type and visa, and the exhibit list whose letterhead and layout every generated Exhibit List follows. Without a letter template, Casey follows Firm Instructions, below, and picks from Firm Knowledge."
      testId="case-prep-drafting"
    >
      {error ? (
        <div
          className="flex items-center justify-between gap-4 px-4 py-3 text-sm text-danger"
          data-testid="drafting-error"
        >
          {error}
          <Button variant="secondary" size="sm" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : entries === null ? (
        <div className="flex flex-col gap-2 px-4 py-3">
          <Skeleton className="h-4 w-1/2 bg-fill-2" />
          <Skeleton className="h-4 w-1/3 bg-fill-2" />
        </div>
      ) : (
        <ul
          className="flex flex-col divide-y divide-line-1"
          data-testid="drafting-groups"
        >
          {groups.length === 0 ? (
            <li>
              <EmptyState
                layout="inline"
                icon={FileText}
                title="No Drafting Templates Yet"
                description="Add letters to Firm Knowledge with a letter type, then mark the default here."
                data-testid="drafting-empty"
              />
            </li>
          ) : null}
          {groups.map((group) => (
            <li key={group.key} data-testid={`drafting-group-${group.key}`}>
              <CasePrepRow
                title={
                  <span className="flex items-center gap-2">
                    {group.label}
                    <Tag>{group.visa ?? "Any Visa"}</Tag>
                  </span>
                }
              >
                <ul className="flex flex-col gap-1">
                  {group.entries.map((entry) => (
                    <EntryRow
                      key={entry.id}
                      entry={entry}
                      group={group}
                      canEdit={canEdit}
                      busy={busy === group.key}
                      onMark={(g, id) => void mark(g, id)}
                      onNavigate={onNavigate}
                    />
                  ))}
                </ul>
              </CasePrepRow>
            </li>
          ))}
          <li data-testid={`drafting-group-${exhibitLists.key}`}>
            <CasePrepRow
              title={
                <span className="flex items-center gap-2">
                  {exhibitLists.label}
                  <Tag>Any Visa</Tag>
                </span>
              }
              description="The packet build and Casey write the list in this document's letterhead, page setup and fonts. Its rows always come from the case."
            >
              <ul className="flex flex-col gap-1">
                <li className="flex min-h-6 items-center justify-between gap-4">
                  <span className="truncate text-sm text-fg-2">
                    {STANDARD_LIST}
                  </span>
                  <DefaultChoice
                    isDefault={exhibitLists.defaultId === null}
                    canEdit={canEdit && exhibitLists.defaultId !== null}
                    busy={busy === exhibitLists.key}
                    onToggle={() => void mark(exhibitLists, null)}
                    testId="drafting-mark-standard"
                  />
                </li>
                {exhibitLists.entries.map((entry) => (
                  <EntryRow
                    key={entry.id}
                    entry={entry}
                    group={exhibitLists}
                    canEdit={canEdit}
                    busy={busy === exhibitLists.key}
                    onMark={(g, id) => void mark(g, id)}
                    onNavigate={onNavigate}
                  />
                ))}
              </ul>
              {exhibitLists.entries.length === 0 ? (
                <span
                  className="text-sm text-fg-3"
                  data-testid="drafting-exhibit-list-hint"
                >
                  Add a Word exhibit list to Firm Knowledge with the Exhibit
                  List type to use your firm&apos;s style.
                </span>
              ) : null}
            </CasePrepRow>
          </li>
        </ul>
      )}
    </CasePrepSection>
  );
};
